import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, MessageSquare, ThumbsUp, ThumbsDown, ChevronUp, ChevronDown, X, MessageCircle } from 'lucide-react';
import { EnrichedRiskArea } from '../types/prevention';
import { Toast } from './Toast';
interface RiskAIChatProps {
  context: 'general' | 'specific';
  riskArea?: EnrichedRiskArea | null;
  className?: string;
  defaultExpanded?: boolean;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  feedback?: 'up' | 'down' | null;
}
export function RiskAIChat({
  context,
  riskArea,
  className = '',
  defaultExpanded = false
}: RiskAIChatProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackType, setFeedbackType] = useState('Risk assessment feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [activeMessageFeedback, setActiveMessageFeedback] = useState<string | null>(null);
  const [messageFeedbackText, setMessageFeedbackText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (isExpanded) {
      scrollToBottom();
    }
  }, [messages, isExpanded, isTyping]);
  const specificPrompts = riskArea ? [`Why is this area rated as ${riskArea.riskLevel}?`, "What would happen if we don't address this risk?", 'Are there similar risks at other sites?', "What's the historical incident rate for this type of risk?"] : [];
  const generalPrompts = ['Which risk area should I prioritize first?', 'Are there any emerging risk patterns across sites?', "Summarize today's critical risks", 'What risks are weather-related?'];
  const prompts = context === 'specific' ? specificPrompts : generalPrompts;
  const generateMockResponse = (userQuery: string): string => {
    const lowerQuery = userQuery.toLowerCase();
    if (context === 'specific' && riskArea) {
      if (lowerQuery.includes('why') || lowerQuery.includes('rated')) {
        return `This area is rated as ${riskArea.riskLevel} primarily due to: ${riskArea.riskFactors[0]}. The combination of these factors significantly increases the probability of an incident based on our historical models.`;
      }
      if (lowerQuery.includes('happen') || lowerQuery.includes('address')) {
        return `If left unaddressed, the likelihood of a safety incident (such as a fall or material drop) increases by 40% over the next 48 hours, potentially leading to work stoppage or injuries.`;
      }
      if (lowerQuery.includes('similar') || lowerQuery.includes('other sites')) {
        return `Yes, we are seeing similar risk patterns at 2 other sites currently, mostly related to weather conditions and delayed inspections.`;
      }
      return `Based on the data for ${riskArea.project} at ${riskArea.location}, I recommend focusing on the suggested corrective actions immediately to mitigate this ${riskArea.riskLevel} risk.`;
    } else {
      if (lowerQuery.includes('prioritize') || lowerQuery.includes('first')) {
        return `You should prioritize the Critical risk at Court Square LIC (East Wing Scaffolding) first. It has the highest risk score (92) due to incoming high winds and overdue inspections.`;
      }
      if (lowerQuery.includes('pattern') || lowerQuery.includes('emerging')) {
        return `I'm detecting an emerging pattern of delayed scaffolding inspections across multiple sites, combined with upcoming adverse weather conditions.`;
      }
      if (lowerQuery.includes('weather')) {
        return `There are currently 2 hotspots related to weather: High winds at Court Square LIC and rain forecasted affecting the concrete pour.`;
      }
      return `I've analyzed the current risk landscape across your selected projects. There are currently 2 Critical and 2 High risk areas requiring attention.`;
    }
  };
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: generateMockResponse(text),
        feedback: null
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1500);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(query);
    }
  };
  const handleMessageFeedback = (msgId: string, type: 'up' | 'down') => {
    setMessages(prev => prev.map(msg => msg.id === msgId ? {
      ...msg,
      feedback: type
    } : msg));
    if (type === 'up') {
      setToastMessage('Thanks for the feedback!');
      setShowToast(true);
      setActiveMessageFeedback(null);
    } else {
      setActiveMessageFeedback(msgId);
      setMessageFeedbackText('');
    }
  };
  const submitMessageFeedback = () => {
    setToastMessage('Feedback submitted. Thank you!');
    setShowToast(true);
    setActiveMessageFeedback(null);
  };
  const submitGeneralFeedback = () => {
    if (!feedbackText.trim()) return;
    setToastMessage('Feedback submitted. Thank you!');
    setShowToast(true);
    setShowFeedbackForm(false);
    setFeedbackText('');
  };
  if (!isExpanded) {
    return <div className={className} data-id="element-3070" data-mp="i6ntssfdawsy">
        <button onClick={() => setIsExpanded(true)} className="w-full flex items-center justify-between p-3 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 rounded-lg transition-colors group" data-id="element-3071" data-mp="i5ma5zkad1zm">
          <div className="flex items-center gap-2" data-id="element-3072" data-mp="r4znx4fg0cq5">
            <Sparkles size={16} className="text-indigo-600" data-id="element-3073" data-mp="w541omoyutco" />
            <span className="text-sm font-medium text-indigo-900" data-id="element-3074" data-mp="3vlaj4me1jb3u">
              {context === 'specific' ? 'Ask AI about this risk' : 'Ask AI about risks'}
            </span>
          </div>
          <ChevronDown size={16} className="text-indigo-400 group-hover:text-indigo-600 transition-colors" data-id="element-3075" data-mp="8g2suyc81g6v" />
        </button>
      </div>;
  }
  return <div className={`flex flex-col bg-white border border-indigo-100 rounded-lg shadow-sm overflow-hidden ${className}`} data-id="element-3076" data-mp="3bxkz51ccif56">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-50" data-id="element-3077" data-mp="3gul4tb5a9fvr">
        <div className="flex items-center gap-2" data-id="element-3078" data-mp="34dsdo80vema0">
          <div className="bg-indigo-600 p-1 rounded-md" data-id="element-3079" data-mp="34f3ga4ikxnzj">
            <Sparkles size={14} className="text-white" data-id="element-3080" data-mp="2zefj113rcawv" />
          </div>
          <span className="text-sm font-bold text-gray-900" data-id="element-3081" data-mp="2zgjvw8jmwk9a">
            BiltonAI · Risk Assistant
          </span>
        </div>
        <button onClick={() => setIsExpanded(false)} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" data-id="element-3082" data-mp="2qeiztf050497">
          <ChevronUp size={16} data-id="element-3083" data-mp="2llhk54s3w0ok" />
        </button>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 max-h-[300px] min-h-[200px] bg-gray-50/50" data-id="element-3084" data-mp="2gm4wwixborlg">
        {messages.length === 0 ? <div className="space-y-3" data-id="element-3085" data-mp="27n9p52emr9g1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-3086" data-mp="27m83y53wh6kl">
              Suggested Questions
            </p>
            {prompts.map((prompt, index) => <button key={index} onClick={() => handleSend(prompt)} className="w-full text-left p-2.5 rounded-lg border border-indigo-100 bg-white hover:bg-indigo-50 hover:border-indigo-200 transition-all text-sm text-gray-700 flex items-start gap-2 group shadow-sm" data-id="element-3087" data-mp="3qmfqnktk4s07" data-mpi={index}>
                <MessageSquare size={14} className="text-indigo-400 mt-0.5 flex-shrink-0 group-hover:text-indigo-600 transition-colors" data-id="element-3088" data-mp="3lo4f95nm69fv" />
                <span data-id="element-3089" data-mp="3cmo774k9ujet">{prompt}</span>
              </button>)}
          </div> : <div className="space-y-4" data-id="element-3090" data-mp="2xya20mzduiv3">
            {messages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-id="element-3091" data-mp="32wbolhw0p0bb">
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-white border border-gray-200 shadow-sm text-gray-800 rounded-bl-none'}`} data-id="element-3092" data-mp="2tvdh2pmdeanz">
                  {msg.text}
                </div>

                {/* AI Message Feedback */}
                {msg.role === 'assistant' && <div className="mt-1 flex items-center gap-1 ml-2" data-id="element-3093" data-mp="2f8eqsxz91mau">
                    <button onClick={() => handleMessageFeedback(msg.id, 'up')} className={`p-1 rounded hover:bg-gray-200 transition-colors ${msg.feedback === 'up' ? 'text-emerald-600' : 'text-gray-400'}`} title="Good response" data-id="element-3094" data-mp="2fa9bqcaiteex">
                      <ThumbsUp size={12} data-id="element-3095" data-mp="2xyo2cygxeqf7" />
                    </button>
                    <button onClick={() => handleMessageFeedback(msg.id, 'down')} className={`p-1 rounded hover:bg-gray-200 transition-colors ${msg.feedback === 'down' ? 'text-red-600' : 'text-gray-400'}`} title="Bad response" data-id="element-3096" data-mp="2y0r4ggvcsa0a">
                      <ThumbsDown size={12} data-id="element-3097" data-mp="20rcj8eq430kt" />
                    </button>
                  </div>}

                {/* Negative Feedback Input */}
                <AnimatePresence data-id="element-3098" data-mp="1r081j791x1ma">
                  {activeMessageFeedback === msg.id && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="mt-2 w-full max-w-[85%] bg-white border border-gray-200 rounded-lg p-2 shadow-sm" data-id="element-3099" data-mp="1m1y3movxzmwz">
                      <p className="text-xs font-medium text-gray-700 mb-1" data-id="element-3100" data-mp="tc7oqdmhkgzt">
                        What was wrong with this response?
                      </p>
                      <textarea value={messageFeedbackText} onChange={e => setMessageFeedbackText(e.target.value)} className="w-full text-xs p-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none" rows={2} placeholder="Optional details..." data-id="element-3101" data-mp="3514d92hqlryk" />
                      <div className="flex justify-end gap-2 mt-2" data-id="element-3102" data-mp="3som1wp5pg9xb">
                        <button onClick={() => setActiveMessageFeedback(null)} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-3103" data-mp="3sqyr9ndslmke">
                          Cancel
                        </button>
                        <button onClick={submitMessageFeedback} className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700" data-id="element-3104" data-mp="23bilmhz2uiz9">
                          Submit
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}

            {isTyping && <div className="flex justify-start" data-id="element-3105" data-mp="257659z40plbu">
                <div className="bg-white border border-gray-200 shadow-sm p-3 rounded-2xl rounded-bl-none flex gap-1" data-id="element-3106" data-mp="259imga1ybrnu">
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-3107" data-mp="2f5lkb3prt44z" />
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-3108" data-mp="2nxni0u8gg6bk" />
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-3109" data-mp="2xxo60th9ram3" />
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-3110" data-mp="37insxb8nh22a" />
          </div>}
      </div>

      {/* Input Area */}
      <div className="p-3 bg-white border-t border-gray-100" data-id="element-3111" data-mp="3chqu2klieo4z">
        <div className="relative flex items-center" data-id="element-3112" data-mp="2xu5w153hrf3x">
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask a question..." className="w-full pl-3 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all" data-id="element-3113" data-mp="2xscpdpsvouvx" />
          <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-1.5 p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md disabled:opacity-50 disabled:hover:bg-transparent transition-colors" data-id="element-3114" data-mp="2evti761mxh20">
            <Send size={16} data-id="element-3115" data-mp="3i6h5fn5yrlup" />
          </button>
        </div>

        {/* General Feedback Link */}
        <div className="mt-2 flex justify-between items-center" data-id="element-3116" data-mp="3ddnxyrj41zex">
          <button onClick={() => setShowFeedbackForm(!showFeedbackForm)} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors" data-id="element-3117" data-mp="38gxk8aus3fwo">
            <MessageCircle size={10} data-id="element-3118" data-mp="2zeeiju2qb9c0" />
            Send Feedback
          </button>
        </div>

        {/* General Feedback Form */}
        <AnimatePresence data-id="element-3119" data-mp="2uih4xviuoh2x">
          {showFeedbackForm && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="mt-2 pt-2 border-t border-gray-100 space-y-2" data-id="element-3120" data-mp="2pibj4vz9hosr">
              <select value={feedbackType} onChange={e => setFeedbackType(e.target.value)} className="w-full text-xs p-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3121" data-mp="107hezlcr21c8">
                <option data-id="element-3122" data-mp="cbdaswpwcp88">Risk assessment feedback</option>
                <option data-id="element-3123" data-mp="ccobuoryjivn">AI response feedback</option>
                <option data-id="element-3124" data-mp="hapx9gcuvuaa">Feature suggestion</option>
                <option data-id="element-3125" data-mp="hbhsgq2jjq01">Other</option>
              </select>
              <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} placeholder="Tell us more..." className="w-full text-xs p-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none" rows={2} data-id="element-3126" data-mp="m7yjbs14kigt" />
              <div className="flex justify-end gap-2" data-id="element-3127" data-mp="2k8miirqq5st">
                <button onClick={() => setShowFeedbackForm(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1" data-id="element-3128" data-mp="2ix9dscenlp1">
                  Cancel
                </button>
                <button onClick={submitGeneralFeedback} disabled={!feedbackText.trim()} className="text-xs bg-gray-900 text-white px-3 py-1 rounded hover:bg-gray-800 disabled:opacity-50" data-id="element-3129" data-mp="7k4f7luzmat2">
                  Submit
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-3130" data-mp="3k1i0y5vp9qx7" />
    </div>;
}